import React, { useEffect, useState } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import Select, { CssTextField } from 'pages/reference-implementation/components/select';
import { InputLabel } from 'pages/reference-implementation/components/input-label';
import { RIStyles } from 'pages/reference-implementation/Style';
import Btn from 'components/button';
import { commonStyles as style } from 'assets/style';
import DbNameHelpImg, { getDbNameHelperText } from './dbNameHelpers';

const PostgreSqlDatabase = (props) => {
    const { resource, handleDrawerAction } = props;

    const [formData, setFormData] = useState({
        postgreSqlDatabaseVersion: resource?.configureData?.postgreSqlDatabaseVersion || 'PostgreSQL 16',
        postgreSqlDatabaseEdition: resource?.configureData?.postgreSqlDatabaseEdition || 'standard',
        databaseName: resource?.configureData?.databaseName || '',
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isValid, setIsValid] = useState(false);
    const [isNumber, setIsNumber] = useState(false);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.databaseName) {
            newErrors.databaseName = { type: 'required', message: '' };
        } else if (/^[0-9]*$/.test(formData.databaseName)) {
            newErrors.databaseName = { type: 'pattern', message: 'Database name should not start with numerics' };
        } else if (/\s/.test(formData.databaseName)) {
            newErrors.databaseName = { type: 'pattern', message: 'Database Name must not contain spaces' };
        } else if (!/^(?![0-9]*$)([A-Z0-9]){2,8}$/.test(formData.databaseName)) {
            newErrors.databaseName = {
                type: 'pattern',
                message: 'Database name should be alphanumeric of 2-8 chars length and special characters are not allowed',
            };
        }

        setErrors(newErrors);
        return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
    };

    const handleOnSelectChange = (key, value) => {
        setFormData({
            ...formData,
            [key]: value,
        });
        setTouched({
            ...touched,
            [key]: true,
        });
    };

    const checkDBValidation = (val) => {
        if (val && val !== '') {
            if (/^[0-9]*$/.test(val)) {
                setIsNumber(true);
                setIsValid(false);
            } else if (/^(?![0-9]*$)([A-Z0-9]){2,8}$/.test(val)) {
                setIsValid(false);
                setIsNumber(false);
            } else if (/^[a-z]*$/.test(val)) {
                setIsNumber(false);
                setIsValid(false);
            } else {
                setIsNumber(false);
                setIsValid(true);
            }
        } else {
            setIsNumber(false);
            setIsValid(false);
        }
    };

    const handleSubmit = () => {
        setTouched({
            databaseName: true,
        });
        const checkFormValid = validateForm();
        handleDrawerAction('save', formData, checkFormValid);
    };

    const shouldShowError = (field) => {
        return touched[field] && !!errors[field];
    };

    useEffect(() => {
        if (Object.keys(touched).length > 0) {
            validateForm();
        }
    }, [formData]);

    const saveButton = () => (
        <Btn className="custom-btn" variant="contained" sx={style.modalSaveButton} onClick={() => handleSubmit()} data-testid="save-btn">
            Save
        </Btn>
    );

    const cancelButton = () => (
        <Btn className="custom-btn" variant="outlined" sx={style.modalCancelButton} onClick={() => handleDrawerAction('cancel')} data-testid="cancel-btn">
            Cancel
        </Btn>
    );

    return (
        <Box style={RIStyles.tags.wrapper} className="ri-layout">
            <Box sx={RIStyles.tags.contentWrapper}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Select
                                key="postgreSqlDatabaseVersion"
                                label="PostgreSQL Database Version"
                                value={formData.postgreSqlDatabaseVersion}
                                options={[{ label: 'PostgreSQL 16', value: 'PostgreSQL 16' }]}
                                disabled
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Select
                                key="postgreSqlDatabaseEdition"
                                label="PostgreSQL Database Edition"
                                value={formData.postgreSqlDatabaseEdition}
                                options={[{ label: 'Standard', value: 'standard' }]}
                                disabled
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CssTextField
                                size="small"
                                sx={{ width: (theme) => theme.spacing('100%') }}
                                autoComplete="off"
                                label={<InputLabel label="Database Name" isRequired />}
                                placeholder="TPMFLN1P"
                                error={isNumber || isValid || shouldShowError('databaseName')}
                                helperText={getDbNameHelperText(isNumber, isValid, touched, errors)}
                                value={formData.databaseName}
                                onChange={(e) => {
                                    const value = e.target.value.toLocaleUpperCase();
                                    checkDBValidation(value);
                                    handleOnSelectChange('databaseName', value);
                                }}
                                className={isNumber || isValid || shouldShowError('databaseName') ? 'validate' : ''}
                            />
                            <DbNameHelpImg />
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Box className="footer" sx={RIStyles.tags.actionWrapper} gap={2}>
                {saveButton()}
                {cancelButton()}
            </Box>
        </Box>
    );
};

PostgreSqlDatabase.propTypes = {
    resource: PropTypes.any.isRequired,
    handleDrawerAction: PropTypes.func.isRequired,
};

export default PostgreSqlDatabase;
